import { Button, Input as AntdInput, Popconfirm, Switch } from 'antd'
import { DeleteFilled } from '@ant-design/icons'
import TextArea from 'antd/lib/input/TextArea'
import { useMemo } from 'react'
import AlertProvidersCell from './AlertProvidersCell'

const BASIC_COLUMNS = [
  {
    title: 'First Name',
    dataIndex: 'firstName',
    key: 'firstName',
  },
  {
    title: 'Last Name',
    dataIndex: 'lastName',
    key: 'lastName',
  },
  {
    title: 'E-mail',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: 'Role',
    dataIndex: ['role', 'name'],
    key: 'role',
  },
  {
    title: 'Position',
    dataIndex: 'positionLabel',
    key: 'position',
  },
]

export default function useColumns(
  practiceId,
  handleChange,
  practice,
  handleBpAlertsChange,
  alertProviders,
) {
  return useMemo(() => {
    const cols = [...BASIC_COLUMNS]
    cols.push({
      title: 'Can receive alerts?',
      key: 'actions',
      render: (text, record) => (
        <span>
          <Switch
            checked={record.isMedicalProvider}
            checkedChildren="Yes"
            unCheckedChildren="No"
            onChange={enabled => handleChange(record.id, practiceId, enabled)}
          />
        </span>
      ),
    })

    cols.push({
      title: 'Receives alerts for',
      key: 'alert_medical_providers',
      render: (text, record) =>
        record.isMedicalProvider ? (
          <AlertProvidersCell
            value={record.alertMedicalProviderIds || []}
            labelById={alertProviders.labelById}
            onEdit={() => alertProviders.openModal(record)}
          />
        ) : null,
    })

    if (practice && practice.bpProgramEnabled) {
      cols.push({
        title: 'Blood Pressure Alerts',
        key: 'bp_alerts',
        render: (text, record) => (
          <span>
            <Switch
              checked={record.canReceiveBpAlerts}
              checkedChildren="Yes"
              unCheckedChildren="No"
              onChange={enabled =>
                handleBpAlertsChange(record.id, practiceId, enabled)
              }
            />
          </span>
        ),
      })
    }

    return cols
  }, [
    practiceId,
    handleChange,
    practice,
    handleBpAlertsChange,
    alertProviders,
  ])
}

export function useLocationsColumns(
  editingLocationId,
  editedLocationName,
  setEditedLocationName,
  handleEditLocation,
  handleConfirmEdit,
  handleDeleteLocation,
  loading,
) {
  return useMemo(() => {
    const cols = [
      {
        title: 'Location',
        dataIndex: 'name',
        key: 'name',
        width: '60%',
        render: (text, record) => {
          if (editingLocationId === record.id) {
            return (
              <AntdInput
                value={editedLocationName}
                onChange={e => setEditedLocationName(e.target.value)}
              />
            )
          }
          return text
        },
      },

      {
        title: 'Actions',
        key: 'actions',
        render: (text, record) => {
          const isEditing = editingLocationId === record.id
          return (
            <>
              <Button
                type="link"
                icon={isEditing ? 'check' : 'edit'}
                loading={loading === 'update'}
                onClick={() =>
                  isEditing
                    ? handleConfirmEdit(record.id)
                    : handleEditLocation(record.id, record.name)
                }
              />

              <Popconfirm
                title="Are you sure you want to delete this location?"
                onConfirm={() => handleDeleteLocation(record.id)}
                placement="topLeft"
              >
                <Button
                  type="link"
                  icon={<DeleteFilled />}
                  loading={loading === 'delete'}
                />
              </Popconfirm>
            </>
          )
        },
      },
    ]
    return cols
  }, [
    editingLocationId,
    editedLocationName,
    setEditedLocationName,
    loading,
    handleConfirmEdit,
    handleEditLocation,
    handleDeleteLocation,
  ])
}

export function usePhrasesColumns(
  editingPhraseId,
  editedPhraseValue,
  setEditedPhraseValue,
  handleEditPhrase,
  handleConfirmEdit,
  handleDeleteLocation,
  loading,
) {
  return useMemo(() => {
    const cols = [
      {
        title: 'Phrase',
        dataIndex: 'value',
        key: 'value',
        width: '60%',
        render: (text, record) => {
          if (editingPhraseId === record.id) {
            return (
              <TextArea
                value={editedPhraseValue}
                onChange={e => setEditedPhraseValue(e.target.value)}
              />
            )
          }
          return text
        },
      },

      {
        title: 'Actions',
        key: 'actions',
        render: (text, record) => {
          const isEditing = editingPhraseId === record.id
          return (
            <>
              <Button
                type="link"
                icon={isEditing ? 'check' : 'edit'}
                loading={loading === 'update'}
                onClick={() =>
                  isEditing
                    ? handleConfirmEdit(record.id)
                    : handleEditPhrase(record.id, record.value)
                }
              />

              <Button
                type="link"
                icon={<DeleteFilled />}
                loading={loading === 'delete'}
                onClick={() => handleDeleteLocation(record.id)}
              />
            </>
          )
        },
      },
    ]
    return cols
  }, [
    editingPhraseId,
    editedPhraseValue,
    setEditedPhraseValue,
    loading,
    handleConfirmEdit,
    handleEditPhrase,
    handleDeleteLocation,
  ])
}
